import { Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { THEMES } from "@/data/themes";
import IMAGES from "@/data/images";
import { ARTICLES } from "@/data/articles";
import { PageHeader } from "@/components/site/PageHeader";
import { Reveal } from "@/components/site/Reveal";
import { ArticleCard } from "@/components/site/ArticleCard";
import { ContextualCTA } from "@/components/site/ContextualCTA";
import { ArrowUpRight } from "lucide-react";

const THEME_IMG = {
  "land-and-regulation": "terrace",
  locations: "himalayaRidge",
  "due-diligence": "villaStone",
  "planning-and-development": "valleyAerial",
  "second-homes": "forestMist",
  "investment-research": "dehradun",
  "construction-and-ownership": "villaCliff",
};

export default function Intelligence() {
  useSeo({
    title: "Intelligence",
    description:
      "The seven themes that organise Selling Sunsets research on Uttarakhand property — land, regulation, locations, due diligence, planning, second homes, investment and construction.",
    path: "/intelligence",
  });

  return (
    <>
      <PageHeader
        eyebrow="Intelligence"
        marker="SS·INT"
        title="How we read a property decision"
        intro="Our research is organised around seven themes. Each gathers the questions, context and reading that matter most to a particular dimension of a hill-property decision."
        breadcrumbs={[{ label: "Intelligence" }]}
      />

      <section className="ss-container pb-8">
        <div className="grid md:grid-cols-2 gap-x-10 gap-y-14">
          {THEMES.map((t, i) => {
            const img = IMAGES[THEME_IMG[t.slug]] || IMAGES.heroRidge;
            return (
              <Reveal key={t.slug} delay={Math.min(i * 0.05, 0.25)}>
                <Link to={`/intelligence/${t.slug}`} className="group block" data-testid={`intelligence-card-${t.slug}`}>
                  <div className="ss-hover-grow relative overflow-hidden aspect-[16/9] bg-[#EFECE6]">
                    <img src={img.url} alt={img.alt} loading="lazy" className="h-full w-full object-cover" />
                    <span className="absolute left-4 top-4 font-mono-ss text-[0.7rem] tracking-widest text-[#F7F5F0] mix-blend-difference">{t.index}</span>
                  </div>
                  <div className="pt-5 flex items-start justify-between gap-4">
                    <div>
                      <h2 className="font-display text-2xl sm:text-3xl font-normal text-[#1A1C1E] group-hover:text-[#2D3B32] transition-colors">{t.title}</h2>
                      <p className="mt-2 text-[0.95rem] font-light leading-relaxed text-[#52575D] max-w-md">{t.summary}</p>
                    </div>
                    <ArrowUpRight className="mt-2 h-5 w-5 shrink-0 text-[#8A9098] transition-transform group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-[#1A1C1E]" strokeWidth={1.3} />
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="ss-container pt-24 pb-8">
        <h2 className="font-display text-3xl sm:text-4xl font-light text-[#1A1C1E] mb-10">From the Intelligence Library</h2>
        <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
          {ARTICLES.slice(0, 3).map((a, i) => <ArticleCard key={a.slug} article={a} index={i} />)}
        </div>
      </section>

      <ContextualCTA
        title="Want this applied to your situation?"
        text="Share what you're considering and we'll point you to the intelligence that matters — and help you structure the questions to investigate."
        whatsappMessage="Hi Suhail, I've been reading the Selling Sunsets intelligence and would like to discuss my situation."
      />
    </>
  );
}
